// Desconto no Balcão (pedido do dono, 30/09/2026): o mesmo desconto do Novo
// Pedido do Delivery — % ou R$ sobre os produtos, ou cupom — no rodapé do
// carrinho. Vale para o pagamento (troco, dividir, PIX) e vai junto quando a
// venda vira "Entrega" (features/balcao-entrega leva e o Delivery aplica igual).
// A tela usa os componentes de Mesas (features/mesas-fechamento/DescontoCupom),
// que espelham o Delivery. O servidor recalcula o total com `desconto_manual`
// e revalida o cupom (`cupom_codigo`).
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { itensDoCupom } from "../../novo-pedido-regras/itensDoCupom";

export const SEM_DESCONTO = { tipo: "none", valor: 0, cupom: null };

const reais = (v) => Math.round((Number(v) || 0) * 100) / 100;

/** Quanto sai do subtotal: % sobre os produtos, R$ até o subtotal, cupom pelo valor validado. */
export function montanteDoDesconto(desconto, subtotal) {
  const v = Number(desconto?.valor) || 0;
  if (desconto?.tipo === "percent") return reais(subtotal * Math.min(100, Math.max(0, v)) / 100);
  if (desconto?.tipo === "fixed") return reais(Math.min(subtotal, Math.max(0, v)));
  if (desconto?.tipo === "coupon") return reais(Math.min(subtotal, Number(desconto?.cupom?.discount) || 0));
  return 0;
}

/** Campos do POST /api/pedidos — os mesmos que o Novo Pedido do Delivery manda. */
export function camposDoDesconto(desconto, montante) {
  const tipo = desconto?.tipo;
  if (!tipo || tipo === "none" || !(montante > 0)) return { desconto_tipo: null, desconto_manual: 0, desconto_percentual: null };
  if (tipo === "coupon") {
    return { desconto_tipo: "coupon", desconto_manual: 0, desconto_percentual: null,
      cupom_codigo: String(desconto.cupom?.code || "").toUpperCase(), cupom_desconto: montante };
  }
  return { desconto_tipo: tipo, desconto_manual: montante, desconto_percentual: tipo === "percent" ? Number(desconto.valor) : null };
}

/**
 * Corpo da reconferência do cupom (1 s depois de mudar o carrinho). `modulo` é
 * o canal da venda — "Balcao" ou "Totem". Até 31/10/2026 era "Totem" fixo: cupom
 * criado só para o Balcão (depois de 24/10, quando os módulos se separaram)
 * passava na 1ª validação e sumia do pedido 1 s depois ("não vale mais").
 */
export function corpoDaReconferencia(codigo, subtotal, cliente, itens, modulo) {
  return {
    code: codigo, order_total: subtotal, customer_id: cliente?.id || null, customer_phone: cliente?.telefone || null,
    ...(itens?.length ? { items: itensDoCupom(itens), modulo } : {}),
  };
}

export function useDescontoBalcao({ ativo, subtotal, itens, modulo = "Balcao" }) {
  const [desconto, setDesconto] = useState(SEM_DESCONTO);
  const clienteRef = useRef(null); // cliente escolhido (para o cupom de cliente), posto pelo TotemApp
  // Carrinho da vez: vai junto na conferência pro servidor tirar da conta do cupom
  // o item em promoção que não aceita cupom (achado 411).
  const itensRef = useRef(itens);
  itensRef.current = itens;
  const montante = ativo ? montanteDoDesconto(desconto, subtotal) : 0;

  // Carrinho vazio (venda concluída, "cancelar esta compra", passagem para a
  // Entrega): o desconto some junto.
  useEffect(() => { if (!(subtotal > 0)) setDesconto(SEM_DESCONTO); }, [subtotal]);

  // Cupom vale sobre o valor da hora em que foi validado: mudou o carrinho,
  // valida de novo (1 s depois da última mudança). Deixou de valer → sai.
  const codigo = desconto.tipo === "coupon" ? desconto.cupom?.code : null;
  useEffect(() => {
    if (!codigo || !(subtotal > 0)) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post("/api/coupons/validate",
          corpoDaReconferencia(codigo, subtotal, clienteRef.current, itensRef.current, modulo));
        if (data?.valid) setDesconto((d) => (d.tipo === "coupon" ? { ...d, cupom: { ...data, code: data.code || codigo } } : d));
        else { setDesconto(SEM_DESCONTO); toast.error(data?.message || `Cupom ${codigo} não vale mais para este pedido`); }
      } catch { /* sem rede: fica o valor anterior; o servidor confere na hora de gravar */ }
    }, 1000);
    return () => clearTimeout(t);
  }, [codigo, subtotal, modulo]);

  return {
    desconto, setDesconto, montante, clienteRef,
    limparDesconto: () => setDesconto(SEM_DESCONTO),
    campos: camposDoDesconto(desconto, montante),
  };
}
